import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// Survives React Strict Mode remounts within the same page load.
let authCallbackStarted = false;

async function waitForSession(timeoutMs = 8000) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) return session;
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
  return null;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { syncSteamData } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function completeSignIn() {
      const session = await waitForSession();
      if (cancelled || !session) return false;

      await syncSteamData();
      navigate('/friends', { replace: true });
      return true;
    }

    async function handleCallback() {
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params.get('token_hash');
      const type = params.get('type');

      // Strict Mode remount: first run already consumed the URL token.
      if (authCallbackStarted) {
        if (await completeSignIn()) return;
        if (!cancelled) setError('Missing authentication token.');
        return;
      }

      if (!tokenHash || !type) {
        if (await completeSignIn()) return;
        if (!cancelled) setError('Missing authentication token.');
        return;
      }

      authCallbackStarted = true;
      window.history.replaceState({}, '', '/auth/callback');

      const { error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
      if (cancelled) return;

      if (verifyError) {
        if (await completeSignIn()) return;
        setError(verifyError.message);
        return;
      }

      await completeSignIn();
    }

    handleCallback();

    return () => {
      cancelled = true;
    };
  }, [navigate, syncSteamData]);

  if (error) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={2} className="min-h-[50vh] px-4 text-center">
        <Typography color="error">{error}</Typography>
        <Typography className="text-slate-400">
          Try signing in again from the home page.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" justifyContent="center" spacing={2} className="min-h-[50vh]">
      <CircularProgress color="primary" />
      <Typography className="text-slate-400">Signing you in with Steam…</Typography>
    </Stack>
  );
}
